import "./CitationPopover.css";
import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { formatCitationExcerpt } from "../helpers/citations";
import { toReadableCitationText } from "../helpers/citationEnrichment";

// Same quick view as the web app's citation popup (CitationHoverPopup).
const POPOVER_MAX_WIDTH = 400;
const POPOVER_VIEWPORT_MARGIN = 8;
const POPOVER_GAP = 10;

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));

/**
 * Places the measured card above the pill when it fits, otherwise below it,
 * and keeps it fully inside the (often narrow) sidebar.
 */
export const getPopoverPosition = (anchorRect, { width, height }, viewport) => {
  const { innerWidth, innerHeight } = viewport;
  const left = clamp(
    anchorRect.left + anchorRect.width / 2 - width / 2,
    POPOVER_VIEWPORT_MARGIN,
    innerWidth - width - POPOVER_VIEWPORT_MARGIN
  );
  const aboveTop = anchorRect.top - POPOVER_GAP - height;
  const belowTop = anchorRect.bottom + POPOVER_GAP;
  const preferredTop = aboveTop >= POPOVER_VIEWPORT_MARGIN ? aboveTop : belowTop;
  const top = clamp(
    preferredTop,
    POPOVER_VIEWPORT_MARGIN,
    innerHeight - height - POPOVER_VIEWPORT_MARGIN
  );

  return { left, top };
};

/**
 * Quick view for an inline citation pill (DEV-6130): the source document, the
 * cited passage, and "See in Document", which opens the document at the cited
 * page.
 *
 * @param {Object} props
 * @param {{ number: number, documentName: string, documentUrl?: string, excerpt?: string, searchTerm?: string }} props.citation
 * @param {DOMRect} props.anchorRect - Position of the pill that opened it
 * @param {(citation: Object) => void} props.onOpenDocument
 * @param {() => void} props.onMouseEnter
 * @param {() => void} props.onMouseLeave
 */
const CitationPopover = ({ citation, anchorRect, onOpenDocument, onMouseEnter, onMouseLeave }) => {
  const [hasLinkError, setHasLinkError] = useState(false);
  const [position, setPosition] = useState(null);
  const popoverRef = useRef(null);
  const { number, documentName, documentUrl, excerpt, searchTerm } = citation;

  // Measure before showing, so a tall card never opens cut off.
  useLayoutEffect(() => {
    const popover = popoverRef.current;
    if (!popover) {
      return;
    }

    const { width, height } = popover.getBoundingClientRect();
    setPosition(
      getPopoverPosition(anchorRect, { width, height }, {
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
      })
    );
  }, [anchorRect, hasLinkError]);

  const handleOpen = () => {
    if (!documentUrl) {
      setHasLinkError(true);

      return;
    }

    setHasLinkError(false);
    onOpenDocument(citation);
  };

  return createPortal(
    <div
      ref={popoverRef}
      className="citation-popover"
      role="dialog"
      aria-label={`Citation ${number}: ${documentName}`}
      style={{
        maxWidth: `min(${POPOVER_MAX_WIDTH}px, calc(100vw - ${POPOVER_VIEWPORT_MARGIN * 2}px))`,
        ...(position || { left: 0, top: 0, visibility: "hidden" }),
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="citation-popover-title">{documentName || "Source document"}</div>
      <div className="citation-popover-excerpt">
        {formatCitationExcerpt(toReadableCitationText(excerpt), searchTerm)}
      </div>
      <button type="button" className="citation-popover-open" onClick={handleOpen}>
        See in Document
      </button>
      {hasLinkError && (
        <p className="citation-popover-error">
          This document isn&apos;t available to open. It may have been removed or has no
          accessible link.
        </p>
      )}
    </div>,
    document.body
  );
};

export default CitationPopover;
